<template>
    <div class="fileupload">
        <div :class="css">
            <div :id="'drop-' + id" class="fileupload-field">
                <div class="empty-state__bg"></div>
                <div class="empty-state" aria-hidden="true">
                    <div class="empty-state__input">
                        <div class="empty-state__icon mar-t--50">
                            <i class="icon icon-087-upload icon--s4 blue" aria-hidden="true"></i>
                        </div>
                        <div class="empty-state__body">
                            <div v-if="multiple" class="body__cta fs-medium">
                                {{ $t('allg.dropzone.ziehen') }}
                            </div>
                            <div v-else class="body__cta fs-medium">
                                {{ $t('allg.dropzone.ziehen') }}
                            </div>
                        </div>
                        <div class="empty-state__footer mar-t--20">
                            <input :id="id" :key="id" type="file" :multiple="multiple" :accept="acceptTypes" :name="id" @change="addFiles" />
                            <slot name="button">
                                <label class="v-btn v-btn--depressed theme--light v-size--default primary" :for="id">
                                    {{ $t('allg.dropzone.durchsuchen') }}
                                </label>
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- List of files for upload -->
        <div :class="css">
            <div v-if="!instantUpload && showFilelist" class="table table--responsive">
                <div class="table__wrapper">
                    <table data-init="auto" class="table-vertical-align">
                        <tbody>
                            <template v-if="files.length > 0">
                                <tr v-for="(file, key) in files" :key="key">
                                    <td class="sc-gray-mid">{{ file.name }}</td>
                                    <td class="nowhow-table-max-50 sc-gray-mid t-right td-action">
                                        <i class="icon-022-close" style="cursor: pointer" @click="removeFile(key)" />
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <tr>
                                    <td class="sc-gray-mid">
                                        {{ $t('allg.dropzone.notselected') }}
                                    </td>
                                    <td class="nowhow-table-max-50 sc-gray-mid t-right td-action"></td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { ref, onMounted, onBeforeUnmount, defineComponent } from 'vue';

export default defineComponent({
    props: {
        id: { type: String, required: true },
        instantUpload: { type: Boolean, default: false },
        multiple: { type: Boolean, default: true },
        acceptTypes: { type: String, default: '' },
        value: { type: [Array, File, Blob], default: null },
        css: { type: String, default: '' },
        showFilelist: { type: Boolean, default: true }
    },
    setup(props, { emit }) {
        const files = ref<Array<File>>([]);
        let fileUpload: HTMLInputElement | null = null;
        let fileUploadDrop: HTMLElement | null = null;

        const addFiles = (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (!input.files) {
                return;
            }
            addFilelist(input.files);
        };

        const dropFiles = (event: DragEvent) => {
            event.preventDefault();
            if (!event.dataTransfer?.files) {
                return;
            }
            addFilelist(event.dataTransfer.files);
        };

        const addFilelist = (filelist: FileList) => {
            if (filelist.length <= 0) {
                return;
            }

            if (!props.multiple) {
                files.value = [];
            }

            for (let i = 0; i < filelist.length; i++) {
                const file = filelist.item(i);
                if (file && checkFileType(file)) {
                    files.value.push(file);
                }
            }

            emitFile();
            if (fileUpload) {
                fileUpload.value = ''; // Allow re-selecting the same file
            }
        };

        const emitFile = () => {
            console.log('files', files.value);
            emit('update:modelValue', props.multiple ? files.value : files.value[0]);

            if (props.instantUpload) {
                files.value = [];
            }
        };

        const checkFileType = (file: File) => {
            if (props.acceptTypes === '') {
                return true;
            }

            const acceptTypeList = props.acceptTypes.split(',');
            const fileType = file.name.split('.').pop()?.toLowerCase();

            return acceptTypeList.some((type) => type.replace('.', '').toLowerCase() === fileType);
        };

        const removeFile = (key: number) => {
            if (files.value.length <= 0) {
                return;
            }
            files.value.splice(key, 1);

            if (!props.multiple || files.value.length === 0) {
                if (fileUpload) {
                    fileUpload.value = '';
                }
            }

            emitFile();
        };

        const dragenter = (event: Event) => event.preventDefault();
        const dragleave = (event: Event) => event.preventDefault();
        const dragover = (event: Event) => event.preventDefault();

        onMounted(() => {
            fileUpload = document.getElementById(props.id) as HTMLInputElement;
            fileUpload?.addEventListener('change', addFiles);

            fileUploadDrop = document.getElementById('drop-' + props.id) as HTMLElement;
            if (fileUploadDrop) {
                fileUploadDrop.addEventListener('dragenter', dragenter);
                fileUploadDrop.addEventListener('dragleave', dragleave);
                fileUploadDrop.addEventListener('dragover', dragover);
                fileUploadDrop.addEventListener('drop', dropFiles);
            }
        });

        onBeforeUnmount(() => {
            fileUpload?.removeEventListener('change', addFiles);
            if (fileUploadDrop) {
                fileUploadDrop.removeEventListener('dragenter', dragenter);
                fileUploadDrop.removeEventListener('dragleave', dragleave);
                fileUploadDrop.removeEventListener('dragover', dragover);
                fileUploadDrop.removeEventListener('drop', dropFiles);
            }
        });

        return { files, addFiles, dropFiles, removeFile };
    }
});
</script>

<style scoped>
.empty-state input[type='file'] {
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 1000;
    opacity: 0;
    overflow: hidden;
    position: absolute;
}
.fileupload-field input {
    cursor: pointer;
}
.fileupload {
    position: relative;
    height: 100%;
}
.fileupload > * {
    padding: 0;
}
.fileupload div:first-of-type {
    margin-bottom: 0 !important;
}
.fileupload-field .empty-state,
.fileupload-field .empty-state__bg {
    height: 100%;
    width: 100%;
    margin-left: 0;
    border: 1px solid #dde3e7;
    display: flex;
    position: absolute;
    align-items: center;
    justify-content: center;
}
</style>
